// src/hooks/usePageMetadata.ts
import { useEffect } from 'react';
import { useCompanyBranding } from './unified';

/**
 * Hook to dynamically update page title and favicon based on system settings
 */
export function usePageMetadata(pageTitle?: string) {
  const branding = useCompanyBranding();

  useEffect(() => {
    // Update page title
    const companyName = branding.name || 'Career Plus';
    const fullTitle = pageTitle ? `${pageTitle} | ${companyName}` : companyName;
    document.title = fullTitle;

    // Update meta description
    if (branding.description) {
      updateMetaDescription(branding.description);
    }

    // Update favicon if logo is available
    if (branding.logo) {
      updateFavicon(branding.logo);
    }
  }, [branding.name, branding.logo, branding.description, pageTitle]);

  return {
    companyName: branding.name || 'Career Plus',
    tagline: branding.tagline,
    description: branding.description,
    logo: branding.logo,
  };
}

/**
 * Update the meta description dynamically
 */
function updateMetaDescription(description: string) {
  let metaDescription = document.querySelector('meta[name="description"]');
  
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }
  
  metaDescription.setAttribute('content', description);
}

/**
 * Update the favicon dynamically
 */
function updateFavicon(logoUrl: string) {
  // Remove existing favicon links
  const existingLinks = document.querySelectorAll("link[rel*='icon']");
  existingLinks.forEach((link) => link.remove());

  // Add new favicon link
  const link = document.createElement('link');
  link.rel = 'icon';
  link.type = 'image/png';
  link.href = logoUrl;
  document.head.appendChild(link);

  // Also add apple-touch-icon for iOS
  const appleLink = document.createElement('link');
  appleLink.rel = 'apple-touch-icon';
  appleLink.href = logoUrl;
  document.head.appendChild(appleLink);
}
